import React from 'react';
import { Button } from '../common';
import { Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { homeElevatedInset } from '../home/homeDashboardStyles';
import { formatPlayerName, getPlayerNameColor } from '../../utils/playerUtils';
import {
  getMatchupVariant,
  MATCHUP_VARIANT_LABELS,
  MATCHUP_VARIANT_ICONS,
  MATCHUP_VARIANT_COLOURS,
} from '../../services/matchupService';

function OpponentFirstName({ name, gender }) {
  const display = name || 'this player';
  return <span className={getPlayerNameColor(gender)}>{display}</span>;
}

/**
 * Description line for evergreen frequent matchup cards (name uses gender colours).
 */
function FrequentMatchupHeadline({ matchup }) {
  const { wins, losses, total_matches, matches_30d, opponent_first_name, opponent_gender } = matchup;
  const variant = getMatchupVariant(matchup);

  switch (variant) {
    case 'recent':
      return (
        <>
          You&apos;ve played <OpponentFirstName name={opponent_first_name} gender={opponent_gender} />{' '}
          {matches_30d} time{matches_30d !== 1 ? 's' : ''} this month
        </>
      );
    case 'closest':
      return (
        <>
          You and <OpponentFirstName name={opponent_first_name} gender={opponent_gender} /> are tied{' '}
          {wins}–{losses} all-time
        </>
      );
    case 'frequent':
    default:
      return (
        <>
          You&apos;ve played <OpponentFirstName name={opponent_first_name} gender={opponent_gender} />{' '}
          {total_matches} time{total_matches !== 1 ? 's' : ''}
        </>
      );
  }
}

function getFrequentMatchupSubline(matchup) {
  const { wins, losses, total_matches, matches_30d } = matchup;
  const variant = getMatchupVariant(matchup);
  const record = `${wins}W – ${losses}L`;

  if (variant === 'recent' && total_matches > matches_30d) {
    return `Current record: ${record}`;
  }
  if (variant === 'frequent') {
    return `Current record: ${record}`;
  }
  return null;
}

/**
 * Evergreen frequent matchup card (player_opponent_stats strip).
 * @param {boolean} [compact=false] - Matches club overview session carousel tiles (faint inset board).
 */
export function FrequentMatchupCard({ matchup, onViewHistory, className = '', compact = false }) {
  const { opponent_first_name, opponent_last_name, opponent_gender } = matchup;
  const variant = getMatchupVariant(matchup);
  const [bgClass, textClass] = MATCHUP_VARIANT_COLOURS[variant];
  const icon = MATCHUP_VARIANT_ICONS[variant];
  const label = MATCHUP_VARIANT_LABELS[variant];
  const subline = getFrequentMatchupSubline(matchup);

  const opponentPlayer = {
    first_name: opponent_first_name,
    last_name: opponent_last_name,
    gender: opponent_gender,
  };

  return (
    <Card
      variant="default"
      size={compact ? 'small' : 'medium'}
      className={`flex flex-col ${compact ? 'gap-2 shadow-none bg-surface-subtle/40' : `gap-3 ${homeElevatedInset}`} shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-lg shrink-0 ${bgClass} ${textClass}`}
          aria-hidden
        >
          {icon}
        </span>
        <Typography
          variant="caption"
          className="text-text-muted uppercase tracking-wide text-[0.65rem] font-semibold"
        >
          {label}
        </Typography>
      </div>

      <div className="flex flex-col gap-1 min-w-0">
        <div className="truncate text-sm font-semibold leading-snug">
          {formatPlayerName(opponentPlayer, 'body-small', 'truncate font-semibold')}
        </div>
        <Typography variant="caption" className="text-text-secondary leading-snug line-clamp-2">
          <FrequentMatchupHeadline matchup={matchup} />
        </Typography>
        {subline ? (
          <Typography variant="caption" className="text-text-muted leading-snug">
            {subline}
          </Typography>
        ) : null}
      </div>

      <div className="mt-auto">
        <Button
          variant="outline"
          size="small"
          className="w-full text-xs font-semibold"
          onClick={() => onViewHistory(matchup)}
        >
          View Match History
        </Button>
      </div>
    </Card>
  );
}
